import React, { FormEvent, useContext } from 'react';
import { useAsyncFn } from 'react-use';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Text, Notification, Input } from '@perch-labs/shrike';
import { useAsyncSetState } from 'hooks/use-async-set-state.hook';
import { copyFor, routes } from 'config';
import { ErrorPage } from 'components/errors';
import { LoadingPage } from 'components/loading';
import { Container, Page } from 'styles';
import { BackLink } from 'components/back-link';
import { useUser } from 'hooks/use-user.hook';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { UPDATE_USER_MUTATION } from '../personal-information.api';
import { validateForm } from './name.utils';

const getCopy = copyFor('settings.name');
const getServerError = copyFor('errors.server');
const { personalInformation } = routes();

export const Name = () => {
  const { addNotification } = useContext(Notification.Context);
  const history = useHistory();
  const { data: user, loading: userLoading, error: userError } = useUser();
  const [mutate] = useMutation(UPDATE_USER_MUTATION);
  const [variables, setVariables] = useAsyncSetState(
    user,
    (stateUser: typeof user) => ({
      currentFirstName: stateUser?.firstName,
      currentLastName: stateUser?.lastName,
      firstName: stateUser?.firstName,
      lastName: stateUser?.lastName,
    })
  );
  const [{ error, loading }, submit] = useAsyncFn(async () => {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'firstName' does not exist on type '{}'.
    const { firstName, lastName } = variables;
    await mutate({
      variables: {
        id: user?.id,
        input: { firstName, lastName },
      },
    });
    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: getCopy('updateNotification'),
      variant: 'success',
    });
    history.push(personalInformation);
  }, [user, variables, mutate, addNotification, history]);
  if (userLoading) return <LoadingPage numberOfSections={1} />;
  if (userError || !user) {
    return <ErrorPage title={getCopy('title')} to={personalInformation} />;
  }
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validateForm({ setVariables, variables })) submit();
  };

  const validateName = (name: string) => {
    const regex = /^[a-zA-ZÀ-ÖØ-öø-ÿ'-]+$/;
    return regex.test(name);
  };

  const valuesChanged =
    (variables as any).firstName !== (variables as any).currentFirstName ||
    (variables as any).lastName !== (variables as any).currentLastName;
  return (
    <>
      <BackLink to={personalInformation} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h2" margin={{ bottom: '48px' }}>
          {getCopy('title')}
        </Text>
        <form onSubmit={handleSubmit}>
          <Container flex direction="column">
            <Input
              errorText={(variables as any).firstNameError}
              label={getCopy('labels.firstName')}
              margin={{ bottom: '16px' }}
              name={getCopy('labels.firstName')}
              onChange={(value: string) => {
                const isValid = validateName(value);
                setVariables({
                  firstName: value,
                  firstNameError: isValid
                    ? null
                    : getCopy('errors.firstNameError'),
                });
              }}
              value={(variables as any).firstName}
            />
            <Input
              errorText={(variables as any).lastNameError}
              label={getCopy('labels.lastName')}
              margin={{ bottom: '16px' }}
              name={getCopy('labels.lastName')}
              onChange={(value: string) => {
                const isValid = validateName(value);
                setVariables({
                  lastName: value,
                  lastNameError: isValid
                    ? null
                    : getCopy('errors.lastNameError'),
                });
              }}
              value={(variables as any).lastName}
            />
          </Container>
          <Alert margin={{ top: '32px' }}>
            {error && getServerError('generic')}
          </Alert>
          <Button
            disabled={
              !valuesChanged ||
              !(variables as any).lastName ||
              !(variables as any).firstName ||
              (variables as any).firstNameError ||
              (variables as any).lastNameError
            }
            fullWidth
            loading={loading}
            margin={{ top: '32px' }}
            type="submit"
          >
            {getCopy('buttons.update')}
          </Button>
        </form>
      </Page>
    </>
  );
};
